// src/services/fhirService.js
// FHIR/VZD integration service for TI-Messenger

import matrixService from '../services/matrixService';
import logger from '../utils/logger';
import { matrixUriToSigil, matrixSigilToUri } from '../utils/fhirAddressConverter';
import { getMatrixOpenIdAccessToken } from './openIdToken';
import { abortWithTimeout } from '../utils/abortWithTimeout';
import { timFetch } from '../utils/timUserAgent';

const SIGIL_VALIDATION = /^@[^:]+:.+$/;
const FETCH_TIMEOUT_MS = 15000;

// FHIR owner-API constants (gematik VZD spec). Exported so tests and
// peer modules can reference them without redeclaring the IG URLs.
const ORIGIN_CS = 'https://gematik.de/fhir/directory/CodeSystem/Origin';
const ORIGIN_OWNER = 'owner';
export const ENDPOINT_VISIBILITY_EXT_URL = 'https://gematik.de/fhir/directory/StructureDefinition/EndpointVisibility';
export const ENDPOINT_VISIBILITY_CS = 'https://gematik.de/fhir/directory/CodeSystem/EndpointVisibilityCS';
export const HIDE_VERSICHERTE = 'hide-versicherte';
// Accept multiple TIM-related connection types used across environments.
const CONNECTION_TYPES = ['tim', 'tim-fa', 'ti-bot'];
const DEFAULT_CONNECTION_TYPE = 'tim';

// ---------------------------------------------------------------------------
// FHIR bundle schema validation (threat-model row 3.2 — TB3 tampering)
//
// VZD-FHIR sits outside our trust boundary; a malicious or compromised server
// could return JSON that parses but whose nested shapes differ from FHIR R4.
// Each `assert*Bundle` below fails-closed by throwing FhirSchemaError before
// the parser walks the structure. We only type-check fields that the
// `_parse*Bundle` methods read — keep these in sync if those parsers start
// reading new fields. Unknown resourceTypes are tolerated (mirrors the
// switch-fallthrough in the parsers); only the type of fields we consume is
// constrained. Missing optional fields are tolerated (parsers default them
// via `?.` / `|| ''`).
// ---------------------------------------------------------------------------

export class FhirSchemaError extends Error {
  constructor(message) {
    super(message);
    this.name = 'FhirSchemaError';
  }
}

function _assertObject(v, where) {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) {
    throw new FhirSchemaError(`${where}: expected object`);
  }
}

function _assertString(v, where) {
  if (typeof v !== 'string') {
    throw new FhirSchemaError(`${where}: expected string`);
  }
}

function _assertArray(v, where) {
  if (!Array.isArray(v)) {
    throw new FhirSchemaError(`${where}: expected array`);
  }
}

function _assertMeta(meta, where) {
  _assertObject(meta, where);
  if (meta.tag !== undefined) {
    _assertArray(meta.tag, `${where}.tag`);
    meta.tag.forEach((t, i) => {
      if (t !== undefined && t !== null) _assertObject(t, `${where}.tag[${i}]`);
    });
  }
}

function _assertPractitionerResource(r, where) {
  if (r.id !== undefined) _assertString(r.id, `${where}.id`);
  if (r.name !== undefined) {
    _assertArray(r.name, `${where}.name`);
    r.name.forEach((n, i) => {
      if (n !== undefined && n !== null) _assertObject(n, `${where}.name[${i}]`);
    });
  }
  if (r.qualification !== undefined) {
    _assertArray(r.qualification, `${where}.qualification`);
    r.qualification.forEach((q, i) => {
      if (q !== undefined && q !== null) _assertObject(q, `${where}.qualification[${i}]`);
    });
  }
}

function _assertEndpointResource(r, where) {
  if (r.id !== undefined) _assertString(r.id, `${where}.id`);
  if (r.name !== undefined) _assertString(r.name, `${where}.name`);
  if (r.address !== undefined) _assertString(r.address, `${where}.address`);
  if (r.status !== undefined) _assertString(r.status, `${where}.status`);
  if (r.connectionType !== undefined) {
    _assertObject(r.connectionType, `${where}.connectionType`);
    if (r.connectionType.code !== undefined) {
      _assertString(r.connectionType.code, `${where}.connectionType.code`);
    }
  }
  if (r.extension !== undefined) {
    _assertArray(r.extension, `${where}.extension`);
    r.extension.forEach((e, i) => {
      if (e !== undefined && e !== null) _assertObject(e, `${where}.extension[${i}]`);
    });
  }
  if (r.meta !== undefined) _assertMeta(r.meta, `${where}.meta`);
}

function _assertPractitionerRoleResource(r, where) {
  if (r.id !== undefined) _assertString(r.id, `${where}.id`);
  if (r.practitioner !== undefined) {
    _assertObject(r.practitioner, `${where}.practitioner`);
    if (r.practitioner.reference !== undefined) {
      _assertString(r.practitioner.reference, `${where}.practitioner.reference`);
    }
  }
  if (r.endpoint !== undefined) {
    _assertArray(r.endpoint, `${where}.endpoint`);
    r.endpoint.forEach((e, i) => {
      if (e !== undefined && e !== null) {
        _assertObject(e, `${where}.endpoint[${i}]`);
        if (e.reference !== undefined) {
          _assertString(e.reference, `${where}.endpoint[${i}].reference`);
        }
      }
    });
  }
  if (r.meta !== undefined) _assertMeta(r.meta, `${where}.meta`);
}

function _assertOrganizationResource(r, where) {
  if (r.id !== undefined) _assertString(r.id, `${where}.id`);
  if (r.name !== undefined) _assertString(r.name, `${where}.name`);
}

function _assertHealthcareServiceResource(r, where) {
  if (r.id !== undefined) _assertString(r.id, `${where}.id`);
  if (r.name !== undefined) _assertString(r.name, `${where}.name`);
  if (r.providedBy !== undefined) {
    _assertObject(r.providedBy, `${where}.providedBy`);
    if (r.providedBy.reference !== undefined) {
      _assertString(r.providedBy.reference, `${where}.providedBy.reference`);
    }
  }
  if (r.endpoint !== undefined) {
    _assertArray(r.endpoint, `${where}.endpoint`);
    r.endpoint.forEach((e, i) => {
      if (e !== undefined && e !== null) {
        _assertObject(e, `${where}.endpoint[${i}]`);
        if (e.reference !== undefined) {
          _assertString(e.reference, `${where}.endpoint[${i}].reference`);
        }
      }
    });
  }
}

function _assertBundle(bundle, where, resourceValidators) {
  _assertObject(bundle, where);
  if (bundle.resourceType !== 'Bundle') {
    throw new FhirSchemaError(
      `${where}.resourceType: expected 'Bundle', got ${JSON.stringify(bundle.resourceType)}`,
    );
  }
  if (bundle.entry === undefined) return;
  _assertArray(bundle.entry, `${where}.entry`);
  bundle.entry.forEach((entry, i) => {
    // Reject null/undefined entries: _parsePractitionerBundle and
    // _parseHealthcareServiceBundle bare-access entry.resource and would
    // throw TypeError otherwise — fail-closed per threat-model row 3.2.
    _assertObject(entry, `${where}.entry[${i}]`);
    if (entry.resource === undefined) return;
    _assertObject(entry.resource, `${where}.entry[${i}].resource`);
    const rt = entry.resource.resourceType;
    if (rt === undefined) return;
    _assertString(rt, `${where}.entry[${i}].resource.resourceType`);
    const validator = resourceValidators[rt];
    if (validator) {
      validator(entry.resource, `${where}.entry[${i}].resource`);
    }
  });
}

export function assertSearchPractitionerBundle(bundle) {
  _assertBundle(bundle, 'PractitionerSearchBundle', {
    Practitioner: _assertPractitionerResource,
    PractitionerRole: _assertPractitionerRoleResource,
    Endpoint: _assertEndpointResource,
  });
}

export function assertSearchHealthcareBundle(bundle) {
  _assertBundle(bundle, 'HealthcareServiceSearchBundle', {
    Organization: _assertOrganizationResource,
    HealthcareService: _assertHealthcareServiceResource,
    Endpoint: _assertEndpointResource,
  });
}

export function assertVisibilityBundle(bundle) {
  _assertBundle(bundle, 'VisibilityBundle', {
    PractitionerRole: _assertPractitionerRoleResource,
    Endpoint: _assertEndpointResource,
  });
}

class FhirService {
  constructor() {
    this._fhirTokenCache = null; // { token, expiresAt }
    this._hbaTokenCache = null;  // { token, expiresAt }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _getBaseUrl() {
    return matrixService.getClient().getHomeserverUrl();
  }

  // Public alias for callers outside this module (hooks, other services).
  getBaseUrl() {
    return this._getBaseUrl();
  }

  // Compute a token cache expiry timestamp.
  // Buffer is 30s for normal lifetimes (>60s); for short lifetimes (≤60s) the
  // buffer is half the lifetime so we never produce an immediately-expired
  // entry. Returns null for missing/invalid/<10s lifetimes — callers must
  // treat null as "do not cache".
  _computeExpiresAt(expiresIn) {
    const seconds = (typeof expiresIn === 'number' && expiresIn > 0) ? expiresIn : 300;
    if (seconds < 10) return null;
    const bufferMs = seconds <= 60 ? (seconds * 500) : 30000;
    return Date.now() + (seconds * 1000) - bufferMs;
  }

  _getUserId() {
    return matrixService.getClient().getUserId();
  }

  _getAccessToken() {
    return matrixService.getClient().getAccessToken();
  }

  _getDisplayName() {
    const user = matrixService.getClient().getUser(this._getUserId());
    return user?.displayName || this._getUserId();
  }

  _authHeaders(token) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json; charset=UTF-8',
    };
  }

  async _rollbackEndpoint(token, baseUrl, endpointId, context, signal) {
    if (!endpointId) return;
    try {
      const ctrl = this._mergedController(signal);
      await this._checkedFetch(
        `${baseUrl}/owner/Endpoint/${encodeURIComponent(endpointId)}`,
        { method: 'DELETE', headers: this._authHeaders(token) },
        ctrl,
      );
      logger.info(`Rolled back created endpoint after ${context}`);
    } catch (rollbackErr) {
      logger.error(`Rollback of created endpoint failed (${context}):`, rollbackErr);
    }
  }

  /**
   * Create an AbortController that fires after FETCH_TIMEOUT_MS or when
   * the caller-provided signal fires, whichever comes first. Thin wrapper
   * around the shared `abortWithTimeout` utility so internal callsites keep
   * the short `this._mergedController(signal)` idiom.
   */
  _mergedController(externalSignal) {
    return abortWithTimeout(externalSignal, FETCH_TIMEOUT_MS);
  }

  async _checkedFetch(url, options, controller) {
    try {
      const response = await timFetch(url, { ...options, signal: controller.signal });
      controller._clearTimeout();
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        const err = new Error(`HTTP ${response.status}: ${body}`);
        err.status = response.status;
        throw err;
      }
      return response;
    } catch (err) {
      controller._clearTimeout();
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Token management
  // ---------------------------------------------------------------------------

  /**
   * Obtain a FHIR bearer token via the Matrix OpenID Connect flow.
   * 1. POST to /_matrix/client/v3/user/{userId}/openid/request_token
   * 2. GET /tim-authenticate?mxId={serverName} with X-Matrix-OpenID-Token header
   * Cached in memory until shortly before expiry.
   */
  async getFhirToken(signal) {
    if (this._fhirTokenCache && Date.now() < this._fhirTokenCache.expiresAt) {
      return this._fhirTokenCache.token;
    }

    const client = matrixService.getClient();
    const baseUrl = this._getBaseUrl();
    const userId = this._getUserId();

    try {
      // Step 1 - obtain an OpenID token via the shared helper (prefers
      // matrix-js-sdk getOpenIdToken, falls back to raw fetch)
      const openIdAccessToken = await getMatrixOpenIdAccessToken(client, {
        signal,
        timeoutMs: FETCH_TIMEOUT_MS,
      });

      // Step 2 - exchange for FHIR token
      const matrixServerName = userId.split(':').slice(1).join(':');
      const ctrl2 = this._mergedController(signal);
      const fhirRes = await this._checkedFetch(
        `${baseUrl}/tim-authenticate?mxId=${encodeURIComponent(matrixServerName)}`,
        {
          method: 'GET',
          headers: {
            'X-Matrix-OpenID-Token': openIdAccessToken,
          },
        },
        ctrl2,
      );
      const fhirData = await fhirRes.json();

      const expiresAt = this._computeExpiresAt(fhirData.expires_in);
      this._fhirTokenCache = expiresAt === null
        ? null
        : { token: fhirData.access_token, expiresAt };

      return fhirData.access_token;
    } catch (err) {
      logger.error('Failed to obtain FHIR token:', err);
      throw err;
    }
  }

  /**
   * Obtain an HBA (Health-professional Bearer Assertion) token.
   * Simple GET to /mock-authenticator.
   */
  async getHbaToken(signal) {
    if (this._hbaTokenCache && Date.now() < this._hbaTokenCache.expiresAt) {
      return this._hbaTokenCache.token;
    }

    const baseUrl = this._getBaseUrl();
    const userId = this._getUserId();
    const localpart = userId?.split(':')[0]?.slice(1);
    const ctrl = this._mergedController(signal);

    try {
      const url = localpart
        ? `${baseUrl}/mock-authenticator?user=${encodeURIComponent(localpart)}`
        : `${baseUrl}/mock-authenticator`;
      const res = await this._checkedFetch(
        url,
        { method: 'GET' },
        ctrl,
      );
      const data = await res.json();

      const expiresAt = this._computeExpiresAt(data.expires_in);
      this._hbaTokenCache = expiresAt === null
        ? null
        : { token: data.access_token, expiresAt };

      return data.access_token;
    } catch (err) {
      logger.error('Failed to obtain HBA token:', err);
      throw err;
    }
  }

  /**
   * Clear all cached tokens. Should be called on logout.
   */
  clearTokens() {
    this._fhirTokenCache = null;
    this._hbaTokenCache = null;
  }

  /**
   * Inject an externally-acquired HBA token into the cache.
   * Used by the authenticator flow (hbaAuthService) so that
   * getOwnVisibility / setGeneralVisibility pick it up without re-fetching.
   * @param {string} accessToken
   * @param {number} [expiresIn=300] seconds
   */
  setHbaToken(accessToken, expiresIn = 300) {
    const expiresAt = this._computeExpiresAt(expiresIn);
    this._hbaTokenCache = expiresAt === null
      ? null
      : { token: accessToken, expiresAt };
  }

  hasValidHbaToken() {
    return !!(this._hbaTokenCache && Date.now() < this._hbaTokenCache.expiresAt);
  }

  hasValidFhirToken() {
    return !!(this._fhirTokenCache && Date.now() < this._fhirTokenCache.expiresAt);
  }

  _extractSubFromJwt(token) {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded.sub || null;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Search API (uses FHIR token)
  // ---------------------------------------------------------------------------

  /**
   * Search for practitioners in the VZD.
   * @param {object} params - Optional search filters:
   *   practitioner.name:contains, practitioner.identifier:contains,
   *   endpoint.name:contains
   * @param {AbortSignal} [signal]
   * @param {object} [options]
   * @param {boolean} [options.excludeHiddenFromInsured=false] - drop endpoints
   *   carrying the EndpointVisibility=hide-versicherte extension; drop the
   *   parent PractitionerRole if no endpoints remain after filtering.
   * @returns {Promise<Array>} Parsed practitioner results
   */
  async searchPractitioners(params = {}, signal, options = {}) {
    const token = await this.getFhirToken(signal);
    const baseUrl = this._getBaseUrl();

    const query = new URLSearchParams({
      '_format': 'json',
    });

    // User-supplied filters applied first
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, value);
      }
    }

    // Defaults applied only when caller did not supply an override.
    // `endpoint:Endpoint.connection-type` defaults to the OR of all known TIM-related
    // types so general searches surface tim, tim-fa, and ti-bot endpoints; pass the
    // key explicitly to narrow scope (e.g. `'tim'` for diagnostic UIs).
    if (!query.has('endpoint:Endpoint.connection-type')) {
      query.set('endpoint:Endpoint.connection-type', CONNECTION_TYPES.join(','));
    }
    if (!query.has('endpoint:Endpoint.status')) {
      query.set('endpoint:Endpoint.status', 'active');
    }
    if (!query.has('practitioner.active')) {
      query.set('practitioner.active', 'true');
    }

    // _include values use append so they add rather than replace
    query.append('_include', 'PractitionerRole:practitioner');
    query.append('_include', 'PractitionerRole:endpoint');

    const ctrl = this._mergedController(signal);
    try {
      const res = await this._checkedFetch(
        `${baseUrl}/search/PractitionerRole?${query.toString()}`,
        {
          method: 'GET',
          headers: this._authHeaders(token),
        },
        ctrl,
      );
      const bundle = await res.json();
      assertSearchPractitionerBundle(bundle);
      return this._parsePractitionerBundle(bundle, options);
    } catch (err) {
      logger.error('Practitioner search failed:', err);
      throw err;
    }
  }

  /**
   * Search for healthcare services in the VZD.
   * @param {object} params - Optional search filters:
   *   name:contains, organization.name:contains, organization.type, address
   * @param {AbortSignal} [signal]
   * @param {object} [options]
   * @param {boolean} [options.excludeHiddenFromInsured=false] - drop endpoints
   *   carrying the EndpointVisibility=hide-versicherte extension; drop the
   *   parent HealthcareService if no endpoints remain after filtering.
   * @returns {Promise<Array>} Parsed healthcare service results
   */
  async searchHealthcareServices(params = {}, signal, options = {}) {
    const token = await this.getFhirToken(signal);
    const baseUrl = this._getBaseUrl();

    const query = new URLSearchParams({
      '_format': 'json',
    });

    // User-supplied filters applied first
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, value);
      }
    }

    // Defaults applied only when caller did not supply an override.
    // See `searchPractitioners` for the connection-type rationale.
    if (!query.has('endpoint:Endpoint.connection-type')) {
      query.set('endpoint:Endpoint.connection-type', CONNECTION_TYPES.join(','));
    }
    if (!query.has('endpoint:Endpoint.status')) {
      query.set('endpoint:Endpoint.status', 'active');
    }
    if (!query.has('organization.active')) {
      query.set('organization.active', 'true');
    }

    // _include values use append so they add rather than replace
    query.append('_include', 'HealthcareService:endpoint');
    query.append('_include', 'HealthcareService:organization');

    const ctrl = this._mergedController(signal);
    try {
      const res = await this._checkedFetch(
        `${baseUrl}/search/HealthcareService?${query.toString()}`,
        {
          method: 'GET',
          headers: this._authHeaders(token),
        },
        ctrl,
      );
      const bundle = await res.json();
      assertSearchHealthcareBundle(bundle);
      return this._parseHealthcareServiceBundle(bundle, options);
    } catch (err) {
      logger.error('Healthcare service search failed:', err);
      throw err;
    }
  }

  /**
   * Search for the current user's LDAP-synced Practitioner via the search API.
   * Called as a last-resort fallback when the owner API cannot find a
   * PractitionerRole for this user (LDAP-origin PRs are invisible to owner queries).
   *
   * @param {string} telematikId - TelematikID from the HBA JWT sub claim
   * @param {AbortSignal} [signal]
   * @returns {Promise<{practitionerRef: string}|null>}
   */
  async _searchOwnPractitioner(telematikId, signal) {
    const token = await this.getFhirToken(signal);
    const baseUrl = this._getBaseUrl();

    const query = new URLSearchParams({
      '_format': 'json',
      'practitioner.identifier': telematikId,
      'practitioner.active': 'true',
    });
    // Deliberately omit endpoint:Endpoint.connection-type and endpoint:Endpoint.status
    // — those would filter out PractitionerRoles that have no TIM endpoints yet.
    query.append('_include', 'PractitionerRole:practitioner');

    const ctrl = this._mergedController(signal);
    try {
      const res = await this._checkedFetch(
        `${baseUrl}/search/PractitionerRole?${query.toString()}`,
        {
          method: 'GET',
          headers: this._authHeaders(token),
        },
        ctrl,
      );
      const bundle = await res.json();

      if (!bundle?.entry?.length) return null;

      // Find the Practitioner whose identifier matches our TelematikID
      const matchingPractitioners = bundle.entry
        .filter(e => e.resource?.resourceType === 'Practitioner')
        .filter(e => (e.resource.identifier || []).some(
          id => id.system === 'https://gematik.de/fhir/sid/telematik-id' &&
                id.value === telematikId,
        ));

      if (matchingPractitioners.length !== 1) {
        if (matchingPractitioners.length > 1) {
          logger.warn(`Ambiguous search result: ${matchingPractitioners.length} Practitioners for TelematikID ${telematikId}`);
        }
        return null;
      }

      return { practitionerRef: `Practitioner/${matchingPractitioners[0].resource.id}` };
    } catch (err) {
      logger.error('Search for own practitioner failed:', err);
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Owner-endpoint predicates and reference parsing
  // ---------------------------------------------------------------------------

  /**
   * Parse a FHIR reference string into an Endpoint resource id.
   * Handles: "Endpoint/xyz", "Endpoint/xyz/_history/3",
   * "https://host/fhir/Endpoint/xyz[/_history/N]", "urn:uuid:abc-123".
   * Returns null for anything unrecognized.
   */
  _extractEndpointId(reference) {
    if (typeof reference !== 'string' || reference.length === 0) return null;

    if (reference.startsWith('urn:uuid:')) {
      const id = reference.slice('urn:uuid:'.length);
      return id.length > 0 ? id : null;
    }

    // Strip trailing /_history/<version>
    const historyStripped = reference.replace(/\/_history\/[^/]+$/, '');

    // Find the last "/Endpoint/" (absolute or relative)
    const marker = '/Endpoint/';
    const idx = historyStripped.indexOf(marker);
    if (idx !== -1) {
      const id = historyStripped.slice(idx + marker.length);
      return id.length > 0 && !id.includes('/') ? id : null;
    }

    // Pure "Endpoint/<id>" (no leading slash)
    if (historyStripped.startsWith('Endpoint/')) {
      const id = historyStripped.slice('Endpoint/'.length);
      return id.length > 0 && !id.includes('/') ? id : null;
    }

    return null;
  }

  /**
   * True iff `ep` is the current user's owner-origin TIM-related endpoint,
   * active, with an address whose sigil equals the caller-provided userSigil.
   * Accepts any of CONNECTION_TYPES — used for *discovery* (visibility detection,
   * stripping references). Destructive operations (DELETE/PUT/reuse) MUST use
   * `_isOwnedTimEndpoint` so we never touch endpoints provisioned by other tools.
   */
  _isMatchingOwnerEndpoint(ep, userSigil) {
    if (!ep || typeof userSigil !== 'string' || userSigil.length === 0) {
      return false;
    }
    const tags = ep.meta?.tag;
    const originOwner = Array.isArray(tags) && tags.some(
      t => t?.system === ORIGIN_CS && t?.code === ORIGIN_OWNER,
    );
    if (!originOwner) return false;
    if (!CONNECTION_TYPES.includes(ep.connectionType?.code)) return false;
    if (ep.status !== 'active') return false;
    if (typeof ep.address !== 'string') return false;
    const sigil = matrixUriToSigil(ep.address);
    return sigil === userSigil;
  }

  /**
   * Strict variant: only true for endpoints whose connectionType is the
   * canonical 'tim' value used by `_buildEndpointBody` on creation.
   * Use this gate before any DELETE/PUT/reuse so we never drive the lifecycle
   * of `tim-fa` / `ti-bot` endpoints that other tooling owns.
   */
  _isOwnedTimEndpoint(entry) {
    return entry?.resource?.connectionType?.code === DEFAULT_CONNECTION_TYPE;
  }

  /**
   * True iff `pr` carries an owner-origin tag.
   */
  _isOwnerPractitionerRole(pr) {
    const tags = pr?.meta?.tag;
    return Array.isArray(tags) && tags.some(
      t => t?.system === ORIGIN_CS && t?.code === ORIGIN_OWNER,
    );
  }

  /**
   * Pick an endpoint id to reuse for idempotent toggle-ON.
   * Precondition: callers pass the array produced by `_parseVisibilityBundle`,
   * which is sorted by id lexicographically. Only endpoints with the canonical
   * `tim` connection type are eligible — adopting a `tim-fa` / `ti-bot`
   * endpoint here would later cause us to delete it on toggle-OFF.
   */
  _pickReusableEndpointId(matchingEndpoints) {
    if (!Array.isArray(matchingEndpoints) || matchingEndpoints.length === 0) {
      return null;
    }
    const reusable = matchingEndpoints.find(entry => this._isOwnedTimEndpoint(entry));
    return reusable?.id ?? null;
  }

  // ---------------------------------------------------------------------------
  // Bundle parsers
  // ---------------------------------------------------------------------------

  /**
   * True iff the Endpoint resource carries an EndpointVisibility extension
   * marking it as `hide-versicherte`. The IG defines `valueCoding.system`
   * at warning severity, so we accept the IG system OR an absent system,
   * but reject any other system to defend against future EndpointVisibility
   * codes accidentally being treated as hidden.
   *
   * @param {object} endpointResource - FHIR Endpoint resource
   * @returns {boolean}
   */
  _isHiddenFromInsured(endpointResource) {
    const exts = endpointResource?.extension;
    if (!Array.isArray(exts)) return false;
    return exts.some(e => {
      if (e?.url !== ENDPOINT_VISIBILITY_EXT_URL) return false;
      if (e?.valueCoding?.code !== HIDE_VERSICHERTE) return false;
      const sys = e.valueCoding?.system;
      return sys === ENDPOINT_VISIBILITY_CS || sys === undefined || sys === null;
    });
  }

  // Map keys are bare endpoint ids; references are canonicalized via
  // `_extractEndpointId` before lookup so absolute URLs, urn:uuid:, and
  // versioned references all resolve to the same Map entry. This avoids
  // a regression where the new excludeHiddenFromInsured filter would
  // silently drop every parent whose endpoints used a non-`Endpoint/<id>`
  // reference form.
  _resolveEndpoints(endpointRefs, endpointsMap) {
    return (endpointRefs || [])
      .map(epRef => {
        const id = this._extractEndpointId(epRef?.reference);
        if (!id) return null;
        const ep = endpointsMap.get(id);
        if (!ep) return null;
        const sigil = matrixUriToSigil(ep.address);
        if (!sigil || !SIGIL_VALIDATION.test(sigil)) return null;
        return {
          id: ep.id,
          name: ep.name || '',
          address: sigil,
          status: ep.status,
          connectionType: ep.connectionType?.code || '',
        };
      })
      .filter(Boolean);
  }

  _parsePractitionerBundle(bundle, { excludeHiddenFromInsured = false } = {}) {
    if (!bundle || !Array.isArray(bundle.entry)) {
      return [];
    }

    const practitioners = new Map();
    const endpoints = new Map();
    const roles = [];

    for (const entry of bundle.entry) {
      const resource = entry.resource;
      if (!resource) continue;

      switch (resource.resourceType) {
        case 'Practitioner':
          practitioners.set(`Practitioner/${resource.id}`, resource);
          break;
        case 'Endpoint':
          if (excludeHiddenFromInsured && this._isHiddenFromInsured(resource)) break;
          endpoints.set(resource.id, resource);
          break;
        case 'PractitionerRole':
          roles.push(resource);
          break;
      }
    }

    return roles.map(role => {
      // Resolve practitioner
      const practRef = role.practitioner?.reference;
      const pract = practRef ? practitioners.get(practRef) : null;

      // Build practitioner display name
      const practName = pract?.name?.[0];
      const displayName = practName
        ? [practName.prefix, practName.given, practName.family]
            .flat()
            .filter(Boolean)
            .join(' ')
        : '';

      // Resolve qualifications
      const qualifications = (pract?.qualification || []).map(q => {
        const coding = q.code?.coding?.[0];
        return coding?.display || coding?.code || '';
      }).filter(Boolean);

      // Resolve endpoints
      const resolvedEndpoints = this._resolveEndpoints(role.endpoint, endpoints);

      return {
        id: role.id,
        practitionerName: displayName,
        qualifications,
        endpoints: resolvedEndpoints,
      };
    }).filter(r => !excludeHiddenFromInsured || r.endpoints.length > 0);
  }

  _parseHealthcareServiceBundle(bundle, { excludeHiddenFromInsured = false } = {}) {
    if (!bundle || !Array.isArray(bundle.entry)) {
      return [];
    }

    const organizations = new Map();
    const endpoints = new Map();
    const services = [];

    for (const entry of bundle.entry) {
      const resource = entry.resource;
      if (!resource) continue;

      switch (resource.resourceType) {
        case 'Organization':
          organizations.set(`Organization/${resource.id}`, resource);
          break;
        case 'Endpoint':
          if (excludeHiddenFromInsured && this._isHiddenFromInsured(resource)) break;
          endpoints.set(resource.id, resource);
          break;
        case 'HealthcareService':
          services.push(resource);
          break;
      }
    }

    return services.map(svc => {
      // Resolve organization
      const orgRef = svc.providedBy?.reference;
      const org = orgRef ? organizations.get(orgRef) : null;

      // Resolve endpoints
      const resolvedEndpoints = this._resolveEndpoints(svc.endpoint, endpoints);

      return {
        id: svc.id,
        serviceName: svc.name || '',
        organizationName: org?.name || '',
        endpoints: resolvedEndpoints,
      };
    }).filter(s => !excludeHiddenFromInsured || s.endpoints.length > 0);
  }

  // ---------------------------------------------------------------------------
  // Owner API (uses HBA token)
  // ---------------------------------------------------------------------------

  /**
   * Get visibility status of the current user's practitioner role in the VZD.
   */
  async getOwnVisibility(signal) {
    let token;
    try {
      token = await this.getHbaToken(signal);
    } catch (err) {
      // No HBA certificate means the user is not a healthcare professional —
      // treat as "not registered" rather than a transient error.
      if (err.message?.includes('No HBA certificate')) {
        return {
          isRegistered: false,
          isGenerallyVisible: false,
          isVisibleToInsurees: false,
          matchingEndpoints: [],
        };
      }
      throw err;
    }
    const baseUrl = this._getBaseUrl();
    const userSigil = this._getUserId();
    const mxidUri = matrixSigilToUri(userSigil);

    if (!userSigil || !mxidUri) {
      throw new Error('Cannot resolve visibility: invalid Matrix user id');
    }

    const headers = this._authHeaders(token);

    // First, try to find PractitionerRole by endpoint address (fast path when endpoint exists)
    const query = new URLSearchParams({
      'endpoint.address': mxidUri,
      '_include': 'PractitionerRole:endpoint',
      '_format': 'json',
    });

    const ctrl = this._mergedController(signal);
    try {
      const res = await this._checkedFetch(
        `${baseUrl}/owner/PractitionerRole?${query.toString()}`,
        { method: 'GET', headers },
        ctrl,
      );
      const bundle = await res.json();
      assertVisibilityBundle(bundle);
      const result = this._parseVisibilityBundle(bundle, userSigil);

      if (result.isRegistered) {
        return result;
      }

      // Endpoint was deleted (e.g. user toggled OFF) — the endpoint.address filter
      // won't match. Fall back to querying by the practitioner's TelematikID
      // (extracted from the JWT sub claim) to find the PractitionerRole without an endpoint.
      const telematikId = this._extractSubFromJwt(token);
      if (!telematikId) {
        logger.warn('Could not extract telematikId from HBA token for fallback query');
        return result;
      }

      const fallbackQuery = new URLSearchParams({
        'practitioner.identifier': telematikId,
        '_include': 'PractitionerRole:endpoint',
        '_format': 'json',
      });
      const ctrl2 = this._mergedController(signal);
      const fallbackRes = await this._checkedFetch(
        `${baseUrl}/owner/PractitionerRole?${fallbackQuery.toString()}`,
        { method: 'GET', headers },
        ctrl2,
      );
      const fallbackBundle = await fallbackRes.json();
      assertVisibilityBundle(fallbackBundle);
      const fallbackResult = this._parseVisibilityBundle(fallbackBundle, userSigil);

      if (fallbackResult.isRegistered) {
        return fallbackResult;
      }

      // Third fallback: use the search API to find an LDAP-synced Practitioner.
      // The owner API cannot find LDAP-origin PractitionerRoles by identifier,
      // but the search API can. If found, the user can auto-register (AF_10058).
      try {
        const searchResult = await this._searchOwnPractitioner(telematikId, signal);
        if (searchResult) {
          return {
            isRegistered: true,
            isGenerallyVisible: false,
            isVisibleToInsurees: false,
            practitionerRoleId: null,
            endpointId: null,
            endpointResource: null,
            matchingEndpoints: [],
            practitionerRef: searchResult.practitionerRef,
          };
        }
      } catch (searchErr) {
        // Search API failure is non-fatal for visibility check
        logger.warn('Search API fallback failed during visibility check:', searchErr);
      }

      return fallbackResult;
    } catch (err) {
      logger.error('Failed to get own visibility:', err);
      throw err;
    }
  }

  /**
   * Parse a VZD owner-API PractitionerRole bundle into visibility state.
   * Uses strict owner+TIM+active+matching-address filtering to defend against
   * leaked endpoints, LDAP-origin resources, and cross-address fallback leakage.
   */
  _parseVisibilityBundle(bundle, userSigil) {
    const empty = {
      isRegistered: false,
      isGenerallyVisible: false,
      isVisibleToInsurees: false,
      practitionerRoleId: null,
      endpointId: null,
      endpointResource: null,
      matchingEndpoints: [],
    };

    if (!bundle || !Array.isArray(bundle.entry) || bundle.entry.length === 0) {
      return empty;
    }

    const prList = [];
    const endpointsMap = new Map();

    for (const entry of bundle.entry) {
      const resource = entry?.resource;
      if (!resource || !resource.resourceType) continue;

      if (resource.resourceType === 'PractitionerRole') {
        prList.push(resource);
      } else if (resource.resourceType === 'Endpoint') {
        endpointsMap.set(resource.id, resource);
      }
    }

    const matchingEndpoints = [...endpointsMap.values()]
      .filter(ep => this._isMatchingOwnerEndpoint(ep, userSigil))
      .map(resource => ({ id: resource.id, resource }))
      .sort((a, b) => a.id.localeCompare(b.id));

    if (matchingEndpoints.length > 1) {
      logger.warn('Multiple matching owner endpoints', {
        count: matchingEndpoints.length,
        ids: matchingEndpoints.map(e => e.id),
      });
    }

    const matchingIds = new Set(matchingEndpoints.map(e => e.id));

    // Primary PR selection: prefer owner-origin PR referencing a matching endpoint;
    // else any owner-origin PR; else any PR (covers LDAP-only pre-registration).
    let primaryPR = prList.find(pr =>
      this._isOwnerPractitionerRole(pr) &&
      (pr.endpoint || []).some(ref => matchingIds.has(this._extractEndpointId(ref?.reference))),
    );
    if (!primaryPR) {
      primaryPR = prList.find(pr => this._isOwnerPractitionerRole(pr));
    }
    if (!primaryPR) {
      primaryPR = prList[0] || null;
    }

    const practitionerRoleId = primaryPR?.id ?? null;
    const isRegistered = primaryPR !== null;

    const primaryMatch = matchingEndpoints[0] ?? null;
    const endpointId = primaryMatch?.id ?? null;
    const endpointResource = primaryMatch?.resource ?? null;

    const isGenerallyVisible = matchingEndpoints.length > 0;

    // Conservative: any matching endpoint carrying a hide marker hides the user.
    const hasHide = matchingEndpoints.some(({ resource }) =>
      (resource.extension || []).some(ext =>
        ext?.url === ENDPOINT_VISIBILITY_EXT_URL &&
        ext?.valueCoding?.code === HIDE_VERSICHERTE,
      ),
    );
    const isVisibleToInsurees = isGenerallyVisible && !hasHide;

    return {
      isRegistered,
      isGenerallyVisible,
      isVisibleToInsurees,
      practitionerRoleId,
      endpointId,
      endpointResource,
      matchingEndpoints,
    };
  }

  /**
   * Set the general visibility of the current user in the VZD.
   * @param {boolean} visible - true to make visible, false to hide
   * @param {object} visibilityState - state returned by getOwnVisibility()
   * @param {AbortSignal} [signal]
   */
  async setGeneralVisibility(visible, visibilityState, signal) {
    const token = await this.getHbaToken(signal);
    const baseUrl = this._getBaseUrl();
    const userSigil = this._getUserId();
    const mxidUri = matrixSigilToUri(userSigil);
    const displayName = this._getDisplayName();

    if (!userSigil || !mxidUri) {
      throw new Error('Cannot set general visibility: invalid Matrix user id');
    }

    if (visible) {
      const { practitionerRoleId } = visibilityState;

      if (!practitionerRoleId && !visibilityState.practitionerRef) {
        throw new Error('Cannot make visible: no practitionerRoleId or practitionerRef in visibility state');
      }

      if (!practitionerRoleId && visibilityState.practitionerRef) {
        // Auto-register flow (AF_10058): no owner PractitionerRole exists yet.
        // Create Endpoint + PractitionerRole together.
        await this._autoRegisterAndMakeVisible(token, baseUrl, mxidUri, displayName, visibilityState, signal);
      } else {
        // Standard flow: PractitionerRole exists. Reuse any already-owned matching
        // endpoint for idempotency; otherwise create a fresh one and link it.
        const matchingEndpoints = visibilityState.matchingEndpoints || [];
        try {
          await this._ensureEndpointLinked(
            token, baseUrl, mxidUri, displayName, practitionerRoleId, matchingEndpoints, signal,
          );
        } catch (linkErr) {
          // The PractitionerRole was deleted on the server (410) since we last fetched
          // visibility state. If the error carries a practitionerRef we can recover by
          // auto-registering a fresh PractitionerRole.
          if (linkErr?.status === 410 && linkErr?.practitionerRef) {
            logger.warn('[fhirService] PractitionerRole was deleted (410); falling back to auto-register', {
              practitionerRef: linkErr.practitionerRef,
            });
            await this._autoRegisterAndMakeVisible(
              token, baseUrl, mxidUri, displayName,
              { ...visibilityState, practitionerRoleId: null, practitionerRef: linkErr.practitionerRef },
              signal,
            );
            return;
          }
          throw linkErr;
        }
      }
    } else {
      // Make invisible: strip ALL matching-endpoint references from the PR,
      // then best-effort DELETE every matching endpoint.
      const { practitionerRoleId } = visibilityState;
      const matchingEndpoints = visibilityState.matchingEndpoints || [];

      if (!practitionerRoleId) {
        throw new Error('Cannot make invisible: missing practitionerRoleId');
      }

      if (matchingEndpoints.length === 0) {
        // Degenerate: PR exists but no owner-TIM endpoints — already invisible.
        logger.info('Set general visibility: already invisible (no matching endpoints)');
        return;
      }

      try {
        // Step 1 - GET the PR and remove every reference that resolves to a matching id
        const ctrl1 = this._mergedController(signal);
        const prRes = await this._checkedFetch(
          `${baseUrl}/owner/PractitionerRole/${encodeURIComponent(practitionerRoleId)}`,
          { method: 'GET', headers: this._authHeaders(token) },
          ctrl1,
        );
        const prResource = await prRes.json();

        const idsToRemove = new Set(matchingEndpoints.map(e => e.id));
        prResource.endpoint = (prResource.endpoint || []).filter(
          ref => !idsToRemove.has(this._extractEndpointId(ref?.reference)),
        );

        const ctrl2 = this._mergedController(signal);
        await this._checkedFetch(
          `${baseUrl}/owner/PractitionerRole/${encodeURIComponent(practitionerRoleId)}`,
          {
            method: 'PUT',
            headers: this._authHeaders(token),
            body: JSON.stringify(prResource),
          },
          ctrl2,
        );

        // Step 2 - Best-effort DELETE each owner-tim endpoint. We never delete
        // `tim-fa` / `ti-bot` endpoints even if they share the user's address —
        // those are owned by other tooling and the PR-strip above already made
        // the user invisible regardless. Honour abort between iterations.
        const deletableEndpoints = matchingEndpoints.filter(entry => this._isOwnedTimEndpoint(entry));
        for (const { id } of deletableEndpoints) {
          if (signal?.aborted) {
            const err = new Error('Aborted');
            err.name = 'AbortError';
            throw err;
          }
          try {
            const ctrlN = this._mergedController(signal);
            await this._checkedFetch(
              `${baseUrl}/owner/Endpoint/${encodeURIComponent(id)}`,
              { method: 'DELETE', headers: this._authHeaders(token) },
              ctrlN,
            );
          } catch (deleteErr) {
            // Only re-throw AbortError if the caller cancelled the whole
            // operation; a per-request 15s timeout is a non-fatal per-call failure.
            if (deleteErr?.name === 'AbortError' && signal?.aborted) throw deleteErr;
            logger.warn('Could not delete Endpoint (may belong to different owner):', deleteErr);
          }
        }

        logger.info('Set general visibility to invisible', {
          deleted: deletableEndpoints.length,
          preserved: matchingEndpoints.length - deletableEndpoints.length,
        });
      } catch (err) {
        logger.error('Failed to set general visibility to invisible:', err);
        throw err;
      }
    }
  }

  /**
   * Idempotent toggle-ON helper: if a matching owner endpoint already exists,
   * reuse it (link into PR if not already referenced); else create fresh via
   * the standard _createEndpointAndLink path.
   */
  async _ensureEndpointLinked(token, baseUrl, mxidUri, displayName, practitionerRoleId, matchingEndpoints, signal) {
    const reusableId = this._pickReusableEndpointId(matchingEndpoints);

    if (!reusableId) {
      await this._createEndpointAndLink(token, baseUrl, mxidUri, displayName, practitionerRoleId, signal);
      return;
    }

    const didLink = await this._linkEndpointToPractitionerRole(token, baseUrl, practitionerRoleId, reusableId, signal);
    if (didLink) {
      logger.info('Set general visibility to visible (reused existing endpoint)', { id: reusableId });
    } else {
      logger.info('Set general visibility: already linked to matching endpoint', { id: reusableId });
    }
  }

  /**
   * GET the PR, append an `Endpoint/<id>` reference if not already present, PUT the PR.
   * Uses `_extractEndpointId` so existing references in versioned/absolute/urn:uuid
   * forms are recognised as "already linked" and not duplicated.
   * Returns true if the PR was modified and PUT, false if the endpoint was already linked.
   */
  async _linkEndpointToPractitionerRole(token, baseUrl, practitionerRoleId, endpointId, signal) {
    const ctrlGet = this._mergedController(signal);
    const prRes = await this._checkedFetch(
      `${baseUrl}/owner/PractitionerRole/${encodeURIComponent(practitionerRoleId)}`,
      { method: 'GET', headers: this._authHeaders(token) },
      ctrlGet,
    );
    const prResource = await prRes.json();

    const alreadyLinked = (prResource.endpoint || []).some(
      ref => this._extractEndpointId(ref?.reference) === endpointId,
    );
    if (alreadyLinked) return false;

    prResource.endpoint = prResource.endpoint || [];
    prResource.endpoint.push({ reference: `Endpoint/${endpointId}` });

    const ctrlPut = this._mergedController(signal);
    try {
      await this._checkedFetch(
        `${baseUrl}/owner/PractitionerRole/${encodeURIComponent(practitionerRoleId)}`,
        {
          method: 'PUT',
          headers: this._authHeaders(token),
          body: JSON.stringify(prResource),
        },
        ctrlPut,
      );
    } catch (putErr) {
      // The PractitionerRole was deleted server-side (410). Attach the practitioner
      // reference extracted from the GET response so callers can re-register.
      if (putErr?.status === 410) {
        putErr.practitionerRef = prResource.practitioner?.reference ?? null;
      }
      throw putErr;
    }
    return true;
  }

  /**
   * Standard flow: create an Endpoint and link it to an existing PractitionerRole.
   */
  async _createEndpointAndLink(token, baseUrl, mxidUri, displayName, practitionerRoleId, signal) {
    let createdEndpointId;
    const ctrl1 = this._mergedController(signal);

    try {
      const endpointBody = this._buildEndpointBody(mxidUri, displayName);

      const createRes = await this._checkedFetch(
        `${baseUrl}/owner/Endpoint`,
        {
          method: 'POST',
          headers: this._authHeaders(token),
          body: JSON.stringify(endpointBody),
        },
        ctrl1,
      );
      const createdEndpoint = await createRes.json();
      createdEndpointId = createdEndpoint.id;

      await this._linkEndpointToPractitionerRole(token, baseUrl, practitionerRoleId, createdEndpointId, signal);

      logger.info('Successfully set general visibility to visible');
    } catch (err) {
      await this._rollbackEndpoint(token, baseUrl, createdEndpointId, 'PractitionerRole update failure', signal);
      // A 410 typically means the PractitionerRole was deleted server-side since
      // the visibility state was last fetched. The caller's 410 fallback path in
      // setGeneralVisibility re-registers automatically, so this is expected.
      if (err?.status === 410) {
        logger.warn('Set general visibility: PractitionerRole 410 (caller will re-register)');
      } else {
        logger.error('Failed to set general visibility to visible:', err);
      }
      throw err;
    }
  }

  /**
   * Auto-register flow (AF_10058): no owner PractitionerRole exists yet.
   * Finds or creates an Endpoint, then creates an owner-origin PractitionerRole
   * referencing the LDAP Practitioner and the Endpoint.
   */
  async _autoRegisterAndMakeVisible(token, baseUrl, mxidUri, displayName, visibilityState, signal) {
    let createdEndpointId;

    try {
      // Step 1 - Create a new Endpoint (always create fresh to ensure we own it;
      // existing Endpoints from prior provisioning may belong to a different owner)
      const ctrl1 = this._mergedController(signal);
      const endpointBody = this._buildEndpointBody(mxidUri, displayName);
      const createRes = await this._checkedFetch(
        `${baseUrl}/owner/Endpoint`,
        {
          method: 'POST',
          headers: this._authHeaders(token),
          body: JSON.stringify(endpointBody),
        },
        ctrl1,
      );
      const createdEndpoint = await createRes.json();
      createdEndpointId = createdEndpoint.id;

      // Step 2 - Create owner-origin PractitionerRole referencing Practitioner + Endpoint
      const prBody = {
        resourceType: 'PractitionerRole',
        practitioner: {
          reference: visibilityState.practitionerRef,
        },
        endpoint: [{ reference: `Endpoint/${createdEndpointId}` }],
        meta: {
          profile: ['https://gematik.de/fhir/directory/StructureDefinition/PractitionerRoleDirectory'],
          tag: [{
            system: ORIGIN_CS,
            code: ORIGIN_OWNER,
          }],
        },
      };

      const ctrl2 = this._mergedController(signal);
      await this._checkedFetch(
        `${baseUrl}/owner/PractitionerRole`,
        {
          method: 'POST',
          headers: this._authHeaders(token),
          body: JSON.stringify(prBody),
        },
        ctrl2,
      );

      logger.info('Successfully auto-registered and set visibility to visible');
    } catch (err) {
      // Rollback: delete the created Endpoint
      await this._rollbackEndpoint(token, baseUrl, createdEndpointId, 'auto-register failure', signal);
      logger.error('Failed to auto-register and make visible:', err);
      throw err;
    }
  }

  _buildEndpointBody(mxidUri, displayName) {
    return {
      resourceType: 'Endpoint',
      status: 'active',
      connectionType: {
        system: 'https://gematik.de/fhir/directory/CodeSystem/EndpointDirectoryConnectionType',
        code: DEFAULT_CONNECTION_TYPE,
      },
      payloadType: [{
        coding: [{
          system: 'https://gematik.de/fhir/directory/CodeSystem/EndpointDirectoryPayloadType',
          code: 'tim-chat',
        }],
      }],
      address: mxidUri,
      name: displayName,
      meta: {
        profile: ['https://gematik.de/fhir/directory/StructureDefinition/EndpointDirectory'],
        tag: [{
          system: ORIGIN_CS,
          code: ORIGIN_OWNER,
        }],
      },
    };
  }

  /**
   * Set insuree visibility for the current user's endpoint.
   * @param {boolean} visible - true to show to insurees, false to hide
   * @param {object} visibilityState - state returned by getOwnVisibility()
   * @param {AbortSignal} [signal]
   */
  async setInsureeVisibility(visible, visibilityState, signal) {
    const token = await this.getHbaToken(signal);
    const baseUrl = this._getBaseUrl();
    const userSigil = this._getUserId();
    const mxidUri = matrixSigilToUri(userSigil);

    if (!userSigil || !mxidUri) {
      throw new Error('Cannot set insuree visibility: invalid Matrix user id');
    }

    const matchingEndpoints = visibilityState.matchingEndpoints || [];
    // Only mutate endpoints we own (canonical 'tim'). PUT-replacing a `tim-fa`
    // or `ti-bot` endpoint provisioned elsewhere would clobber its extensions.
    const writableEndpoints = matchingEndpoints.filter(entry => this._isOwnedTimEndpoint(entry));
    if (writableEndpoints.length === 0) {
      throw new Error('No matching endpoint for insuree-visibility update');
    }

    const failures = [];
    let successes = 0;

    for (const { id, resource } of writableEndpoints) {
      if (signal?.aborted) {
        const err = new Error('Aborted');
        err.name = 'AbortError';
        throw err;
      }

      const updatedEndpoint = JSON.parse(JSON.stringify(resource));

      if (visible) {
        // Remove EndpointVisibility extension via strict URL equality,
        // preserving any unrelated extensions that happen to contain the substring.
        updatedEndpoint.extension = (updatedEndpoint.extension || []).filter(
          ext => ext?.url !== ENDPOINT_VISIBILITY_EXT_URL,
        );
      } else {
        updatedEndpoint.extension = updatedEndpoint.extension || [];
        const hasExtension = updatedEndpoint.extension.some(
          ext => ext?.url === ENDPOINT_VISIBILITY_EXT_URL,
        );
        if (!hasExtension) {
          updatedEndpoint.extension.push({
            url: ENDPOINT_VISIBILITY_EXT_URL,
            valueCoding: {
              system: ENDPOINT_VISIBILITY_CS,
              code: HIDE_VERSICHERTE,
            },
          });
        }
      }

      try {
        const ctrl = this._mergedController(signal);
        await this._checkedFetch(
          `${baseUrl}/owner/Endpoint/${encodeURIComponent(id)}`,
          {
            method: 'PUT',
            headers: this._authHeaders(token),
            body: JSON.stringify(updatedEndpoint),
          },
          ctrl,
        );
        successes += 1;
      } catch (err) {
        // Only re-throw AbortError if the caller cancelled the whole
        // operation; a per-request 15s timeout is a non-fatal per-call failure.
        if (err?.name === 'AbortError' && signal?.aborted) throw err;
        failures.push({ id, error: err });
        logger.warn('Insuree-visibility PUT failed for endpoint', { id, error: err?.message });
      }
    }

    if (failures.length > 0) {
      const errorMessages = failures
        .map(f => f.error?.message || String(f.error))
        .join('; ');
      const aggregate = new Error(errorMessages || 'Insuree-visibility update failed', { cause: failures });
      // Stamp .status when every failure shares the same HTTP status so callers
      // (e.g. useFHIRTokens.runVisibilityUpdate) can branch on a uniform status
      // without scanning err.cause manually. A heterogeneous mix leaves .status
      // unset, which is the safe default — caller will not retry.
      const statuses = failures.map(f => f.error?.status).filter(s => typeof s === 'number');
      if (statuses.length === failures.length && statuses.every(s => s === statuses[0])) {
        aggregate.status = statuses[0];
      }
      throw aggregate;
    }

    logger.info(`Set insuree visibility to ${visible ? 'visible' : 'hidden'}`, {
      updated: successes,
      failed: failures.length,
    });
  }
}

const fhirService = new FhirService();

// Expose globally for the Gematik test driver (dev/test builds only — never
// production). Mirrors the pattern used in matrixService.js for window.__matrixClient.
// The test driver uses this to invoke FHIR-VZD operations (auth, set MXID,
// search) without re-implementing the multi-step token+endpoint+practitioner-role
// flow. Per gematik-test-driver/CLAUDE.md, page.evaluate against these is a
// read-only-state-or-app-service exception to the no-API-circumvention rule.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__fhirService = fhirService;
}

export default fhirService;
